import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card } from '@/components/Card'
import { PermissionGate } from '@/components/PermissionGate'
import { userManagementService, type UserWithPermissions } from '@/services/userManagement'
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX,
  Shield,
  FileText,
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  Utensils
} from 'lucide-react'
import toast from 'react-hot-toast'

export function UserViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<UserWithPermissions | null>(null)
  const [loading, setLoading] = useState(true)

  // Carregar usuário
  useEffect(() => {
    const loadUser = async () => {
      if (!id) return

      try {
        setLoading(true)
        const response = await userManagementService.getUserById(parseInt(id))
        
        if (response.success) {
          setUser(response.data)
        } else {
          toast.error(response.message || 'Usuário não encontrado')
          navigate('/usuarios')
        }
      } catch (error) {
        toast.error('Erro ao carregar usuário')
        console.error('Erro:', error)
        navigate('/usuarios')
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [id, navigate])

  // Deletar usuário
  const handleDeleteUser = async () => {
    if (!user) return

    if (!confirm(`Tem certeza que deseja remover o usuário "${user.nome}"?`)) {
      return
    }

    try {
      const response = await userManagementService.deleteUser(user.id)
      if (response.success) {
        toast.success('Usuário removido com sucesso!')
        navigate('/usuarios')
      } else {
        toast.error(response.message || 'Erro ao remover usuário')
      }
    } catch (error) {
      toast.error('Erro ao remover usuário')
      console.error('Erro:', error)
    }
  }

  // Alternar status
  const handleToggleStatus = async () => {
    if (!user) return

    try {
      const response = await userManagementService.updateUser(user.id, { 
        isActive: !user.isActive 
      })
      
      if (response.success) {
        toast.success(`Usuário ${!user.isActive ? 'ativado' : 'desativado'} com sucesso!`)
        setUser(response.data)
      } else {
        toast.error(response.message || 'Erro ao atualizar usuário')
      }
    } catch (error) {
      toast.error('Erro ao atualizar usuário')
      console.error('Erro:', error)
    }
  }

  // Obter ícone do role
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'administrador':
        return <Shield className="w-5 h-5" />
      case 'vistoria':
        return <FileText className="w-5 h-5" />
      case 'calendario':
        return <Calendar className="w-5 h-5" />
      case 'cardapio':
        return <Utensils className="w-5 h-5" />
      case 'usuario_basico':
        return <User className="w-5 h-5" />
      default:
        return <User className="w-5 h-5" />
    }
  }

  // Obter cor do role
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'administrador':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'vistoria':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'calendario':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'cardapio':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'usuario_basico':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Obter descrição do role
  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'administrador':
        return 'Acesso completo a todas as funcionalidades do sistema'
      case 'vistoria':
        return 'Acesso a dashboard, vistorias e ajuda'
      case 'calendario':
        return 'Acesso a dashboard, calendários e ajuda'
      case 'cardapio':
        return 'Acesso a dashboard, cardápios e ajuda'
      case 'usuario_basico':
        return 'Acesso básico: dashboard e ajuda'
      default:
        return ''
    }
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando usuário...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Usuário não encontrado</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Cabeçalho */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/usuarios')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Voltar"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{user.nome}</h1>
              <p className="text-gray-600">@{user.login}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <PermissionGate permission="user_edit">
              <button
                onClick={handleToggleStatus}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  user.isActive
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {user.isActive ? (
                  <>
                    <UserX className="w-4 h-4" />
                    <span>Desativar</span>
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4" />
                    <span>Ativar</span>
                  </>
                )}
              </button>
            </PermissionGate>
            
            <PermissionGate permission="user_edit">
              <Link
                to={`/usuarios/editar/${user.id}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Editar</span>
              </Link>
            </PermissionGate>
            
            <PermissionGate permission="user_delete">
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Remover</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Informações Básicas */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dados Pessoais */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Pessoais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Nome Completo</label>
                <p className="text-gray-900">{user.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Login</label>
                <p className="text-gray-900">@{user.login}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">ID</label>
                <p className="text-gray-900">#{user.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive ? (
                    <>
                      <UserCheck className="w-3 h-3 mr-1" />
                      Ativo
                    </>
                  ) : (
                    <>
                      <UserX className="w-3 h-3 mr-1" />
                      Inativo
                    </>
                  )}
                </span>
              </div>
              {user.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-gray-900 flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    {user.email}
                  </p>
                </div>
              )}
              {user.telefone && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Telefone</label>
                  <p className="text-gray-900 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    {user.telefone}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Informações do Sistema */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações do Sistema</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {user.createdAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Criado em</label>
                  <p className="text-gray-900 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
              {user.lastLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Último Login</label>
                  <p className="text-gray-900 flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" />
                    {new Date(user.lastLogin).toLocaleString('pt-BR')}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar com Role e Permissões */}
        <div className="space-y-6">
          {/* Role */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Role e Permissões</h3>
            
            <div className="space-y-3">
              {(Array.isArray(user.role) ? user.role : [user.role]).map((role, index) => (
                <div key={index} className={`border rounded-lg p-4 ${getRoleColor(role)}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {getRoleIcon(role)}
                    <span className="font-medium capitalize">
                      {role.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm">
                    {getRoleDescription(role)}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Permissões Incluídas:</h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {(() => {
                  const userRoles = Array.isArray(user.role) ? user.role : [user.role]
                  const permissions = new Set<string>()
                  
                  userRoles.forEach(role => {
                    switch (role) {
                      case 'administrador':
                        permissions.add('• Acesso completo ao sistema')
                        permissions.add('• Gerenciar usuários')
                        permissions.add('• Gerenciar vistorias')
                        permissions.add('• Gerenciar calendários')
                        permissions.add('• Gerenciar cardápios')
                        permissions.add('• Gerenciar geolocalização')
                        permissions.add('• Gerenciar parâmetros')
                        permissions.add('• Visualizar relatórios')
                        permissions.add('• Configurações do sistema')
                        break
                      case 'vistoria':
                        permissions.add('• Visualizar dashboard')
                        permissions.add('• Gerenciar vistorias')
                        permissions.add('• Criar vistorias')
                        permissions.add('• Editar vistorias')
                        break
                      case 'calendario':
                        permissions.add('• Visualizar dashboard')
                        permissions.add('• Gerenciar calendários')
                        permissions.add('• Criar calendários')
                        permissions.add('• Editar calendários')
                        break
                      case 'cardapio':
                        permissions.add('• Visualizar dashboard')
                        permissions.add('• Gerenciar cardápios')
                        permissions.add('• Criar cardápios')
                        permissions.add('• Editar cardápios')
                        break
                      case 'usuario_basico':
                        permissions.add('• Visualizar dashboard')
                        break
                    }
                  })
                  
                  permissions.add('• Acessar ajuda')
                  
                  return Array.from(permissions).map((permission, index) => (
                    <li key={index}>{permission}</li>
                  ))
                })()}
              </ul>
            </div>
          </Card>

          {/* Ações Rápidas */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
            <div className="space-y-3">
              <PermissionGate permission="user_edit">
                <Link
                  to={`/usuarios/editar/${user.id}`}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Usuário
                </Link>
              </PermissionGate>
              
              <PermissionGate permission="user_edit">
                <button
                  onClick={handleToggleStatus}
                  className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${
                    user.isActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {user.isActive ? (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Desativar Usuário
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Ativar Usuário
                    </>
                  )}
                </button>
              </PermissionGate>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
